import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { Organization } from '@/api/models';
import { OrganizationQueryKeys } from './organizationQueryKeys';

/**
 * Finishes onboarding (`POST /organizations/me/onboarding/finish`, OrgAdmin).
 * The server sets `onboardedAt` once, so a repeated call is harmless.
 *
 * Unwraps the envelope (rulebook §10): a refusal rejects with the
 * `ErrorResponse`. On success the organisation is invalidated, so the gate
 * reads it onboarded and opens the shell. The mutation settles only after
 * that refetch, so a caller that navigates home on success does not meet the
 * gate still holding the unfinished organisation.
 */
export const useFinishOnboardingMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<Organization, ErrorResponse, void>({
        mutationFn: async () => {
            const result = await apiClient.organizations.finishOnboarding();
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: OrganizationQueryKeys.me(),
            }),
    });
};
